'use client';
import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import AppNav from '@/components/AppNav';
import { supabase } from '@/lib/supabase/client';

interface BusinessData {
  id: string;
  legal_name: string;
  trade_name: string;
  entity_type: string;
  cin: string | null;
  pan: string | null;
  tan: string | null;
  gstin: string | null;
  setup_progress: number;
  is_demo: boolean;
  founders: Array<{ full_name: string; email: string; pan: string; is_director: boolean; shareholding_percent: number }>;
  registered_offices: Array<{ address_line1: string; address_line2: string; city: string; state: string; pincode: string }>;
  business_activities: Array<{ description: string; nic_code: string; industry: string }>;
  business_passports: { completeness_score: number; msme_number: string | null; dpiit_number: string | null } | null;
}

const entityTypeLabels: Record<string, string> = {
  private_limited: 'Private Limited Company',
  llp: 'Limited Liability Partnership',
  opc: 'One Person Company',
  partnership: 'Partnership Firm',
  sole_proprietorship: 'Sole Proprietorship',
};

const sections = [
  { id: 'identity', label: 'Identity', icon: '◈' },
  { id: 'people', label: 'People', icon: '◉' },
  { id: 'address', label: 'Address', icon: '◎' },
  { id: 'business', label: 'Business', icon: '◇' },
  { id: 'registrations', label: 'Registrations', icon: '✓' },
];

export default function BusinessPage() {
  const [business, setBusiness] = useState<BusinessData | null>(null);
  const [activeSection, setActiveSection] = useState('identity');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function loadBusiness() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setLoading(false); return; }

        const { data, error: bizErr } = await supabase
          .from('businesses')
          .select('*, founders(*), registered_offices(*), business_activities(*), business_passports(*)')
          .eq('user_id', user.id)
          .limit(1)
          .single();

        if (bizErr && bizErr.code !== 'PGRST116') throw bizErr;
        if (data) setBusiness(data as BusinessData);
      } catch (err) {
        console.error('Business load error:', err);
        setError('Failed to load business data. Please refresh.');
      } finally {
        setLoading(false);
      }
    }
    loadBusiness();
  }, []);

  const InfoRow = ({ label, value, placeholder }: { label: string; value: string | null; placeholder?: string }) => (
    <div className="flex items-start justify-between py-3 border-b border-border last:border-0 gap-4">
      <span className="text-[12px] font-medium text-ink-muted w-36 sm:w-40 flex-shrink-0">{label}</span>
      <span className={`text-[13px] font-medium flex-1 text-right break-all ${value ? 'text-ink' : 'text-ink-subtle italic'}`}>
        {value || placeholder || '—'}
      </span>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/business" />
        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
            <div className="animate-pulse space-y-4">
              <div className="h-10 bg-surface-3 rounded-xl w-64" />
              <div className="h-48 bg-white rounded-xl border border-border" />
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/business" />
        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl border border-border p-10 text-center max-w-md w-full">
            <div className="w-14 h-14 bg-red-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-7 h-7 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <h2 className="text-lg font-bold text-ink mb-2">Unable to load</h2>
            <p className="text-[13px] text-ink-muted mb-6">{error}</p>
            <button onClick={() => window.location.reload()} className="bg-ai text-white text-[13px] font-semibold px-5 py-2.5 rounded-xl hover:bg-ai-dark transition-colors">
              Refresh
            </button>
          </div>
        </main>
      </div>
    );
  }

  if (!business) {
    return (
      <div className="min-h-screen bg-surface-2 font-sans">
        <AppNav variant="app" activePath="/business" />
        <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen flex items-center justify-center p-6">
          <div className="bg-white rounded-2xl border border-border p-10 text-center max-w-md w-full">
            <div className="w-16 h-16 bg-ai-light rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-ai" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
              </svg>
            </div>
            <h2 className="text-xl font-bold text-ink mb-2">No Business Passport yet</h2>
            <p className="text-[13px] text-ink-muted mb-6">
              Create your Business Passport to store all your business information in one place. Lexigen AI will use it to pre-fill forms and provide personalised guidance.
            </p>
            <Link
              href="/onboarding"
              className="inline-flex items-center gap-2 bg-ai text-white font-semibold px-6 py-3 rounded-xl hover:bg-ai-dark transition-all text-[14px] shadow-sm"
            >
              Create Business Passport →
            </Link>
          </div>
        </main>
      </div>
    );
  }

  const completeness = business.business_passports?.completeness_score || business.setup_progress || 0;

  return (
    <div className="min-h-screen bg-surface-2 font-sans">
      <AppNav variant="app" activePath="/business" />

      <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">

          {/* Header */}
          <div className="flex items-start justify-between mb-8 gap-4">
            <div>
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="text-2xl font-bold text-ink">{business.legal_name || 'Your Business'}</h1>
                {business.is_demo && (
                  <span className="text-[10px] font-semibold bg-amber-50 text-amber-800 px-2 py-0.5 rounded-full border border-amber-200">DEMO</span>
                )}
              </div>
              <p className="text-ink-muted text-[14px]">{entityTypeLabels[business.entity_type] || business.entity_type}</p>
            </div>
            <div className="text-right flex-shrink-0">
              <div className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wide mb-1">Passport completeness</div>
              <div className="text-2xl font-bold text-ink">{completeness}%</div>
              <div className="w-24 bg-surface-3 rounded-full h-1.5 mt-2 ml-auto" role="progressbar" aria-valuenow={completeness} aria-valuemin={0} aria-valuemax={100}>
                <div className="bg-ai h-1.5 rounded-full" style={{ width: `${completeness}%` }} />
              </div>
            </div>
          </div>

          <div className="flex gap-6">
            {/* Sidebar Nav */}
            <nav className="hidden md:block w-44 flex-shrink-0" aria-label="Business passport sections">
              <div className="space-y-1 sticky top-8">
                {sections.map(s => (
                  <button
                    key={s.id}
                    onClick={() => setActiveSection(s.id)}
                    aria-current={activeSection === s.id ? 'page' : undefined}
                    className={`w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-[13px] font-medium transition-all text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai ${
                      activeSection === s.id ? 'bg-ai-light text-ai' : 'text-ink-muted hover:text-ink hover:bg-surface-3'
                    }`}
                  >
                    <span aria-hidden="true">{s.icon}</span>
                    {s.label}
                  </button>
                ))}
              </div>
            </nav>

            {/* Content */}
            <div className="flex-1 min-w-0 space-y-6">

              {/* Mobile section tabs */}
              <div className="md:hidden flex gap-2 overflow-x-auto pb-2 -mx-1 px-1" role="tablist" aria-label="Business passport sections">
                {sections.map(s => (
                  <button
                    key={s.id}
                    role="tab"
                    aria-selected={activeSection === s.id}
                    onClick={() => setActiveSection(s.id)}
                    className={`flex-shrink-0 px-3 py-1.5 rounded-lg text-[12px] font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai ${
                      activeSection === s.id ? 'bg-ai text-white' : 'bg-white text-ink-muted border border-border'
                    }`}
                  >
                    {s.label}
                  </button>
                ))}
              </div>

              {/* Identity Section */}
              {activeSection === 'identity' && (
                <div className="bg-white rounded-xl border border-border p-5 sm:p-6">
                  <h2 className="font-semibold text-ink text-[15px] mb-5">Business Identity</h2>
                  <InfoRow label="Legal Name" value={business.legal_name} placeholder="Not set" />
                  <InfoRow label="Trade Name" value={business.trade_name} placeholder="Not set" />
                  <InfoRow label="Entity Type" value={entityTypeLabels[business.entity_type] || null} placeholder="Not set" />
                  <InfoRow label="CIN" value={business.cin} placeholder="Pending registration" />
                  <InfoRow label="PAN" value={business.pan} placeholder="Pending" />
                  <InfoRow label="TAN" value={business.tan} placeholder="Pending" />
                  <InfoRow label="GSTIN" value={business.gstin} placeholder="Not registered" />
                </div>
              )}

              {/* People Section */}
              {activeSection === 'people' && (
                <div className="space-y-4">
                  <div className="bg-white rounded-xl border border-border p-5 sm:p-6">
                    <div className="flex items-center justify-between mb-5">
                      <h2 className="font-semibold text-ink text-[15px]">Founders & Directors</h2>
                      <span className="text-[12px] text-ink-muted">{business.founders?.length || 0} people</span>
                    </div>
                    {!business.founders?.length && (
                      <div className="text-center py-6">
                        <p className="text-[13px] text-ink-muted">No founders added yet.</p>
                        <Link href="/onboarding" className="text-[12px] text-ai hover:text-ai-dark mt-2 inline-block">Add founders →</Link>
                      </div>
                    )}
                    {business.founders?.map((founder, i) => (
                      <div key={i} className="flex items-start gap-4 py-4 border-b border-border last:border-0">
                        <div className="w-9 h-9 bg-ai-light rounded-full flex items-center justify-center flex-shrink-0" aria-hidden="true">
                          <span className="text-ai font-semibold text-[13px]">{founder.full_name.charAt(0)}</span>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold text-ink text-[14px]">{founder.full_name}</p>
                          <p className="text-[12px] text-ink-muted truncate">{founder.email}</p>
                          <div className="flex gap-2 mt-2 flex-wrap">
                            {founder.is_director && <span className="text-[10px] font-semibold bg-ai-light text-ai px-2 py-0.5 rounded-full">Director</span>}
                            {founder.shareholding_percent > 0 && (
                              <span className="text-[10px] font-semibold bg-surface-3 text-ink-muted px-2 py-0.5 rounded-full">{founder.shareholding_percent}% shares</span>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Address Section */}
              {activeSection === 'address' && (
                <div className="bg-white rounded-xl border border-border p-5 sm:p-6">
                  <h2 className="font-semibold text-ink text-[15px] mb-5">Registered Office</h2>
                  {!business.registered_offices?.length && (
                    <div className="text-center py-6">
                      <p className="text-[13px] text-ink-muted">No address added yet.</p>
                      <Link href="/onboarding" className="text-[12px] text-ai hover:text-ai-dark mt-2 inline-block">Add address →</Link>
                    </div>
                  )}
                  {business.registered_offices?.map((office, i) => (
                    <div key={i}>
                      <InfoRow label="Address Line 1" value={office.address_line1} />
                      <InfoRow label="Address Line 2" value={office.address_line2} />
                      <InfoRow label="City" value={office.city} />
                      <InfoRow label="State" value={office.state} />
                      <InfoRow label="Pincode" value={office.pincode} />
                    </div>
                  ))}
                </div>
              )}

              {/* Business Activity Section */}
              {activeSection === 'business' && (
                <div className="bg-white rounded-xl border border-border p-5 sm:p-6">
                  <h2 className="font-semibold text-ink text-[15px] mb-5">Business Activity</h2>
                  {!business.business_activities?.length && (
                    <div className="text-center py-6">
                      <p className="text-[13px] text-ink-muted">No business activity added yet.</p>
                      <Link href="/onboarding" className="text-[12px] text-ai hover:text-ai-dark mt-2 inline-block">Add activity →</Link>
                    </div>
                  )}
                  {business.business_activities?.map((activity, i) => (
                    <div key={i}>
                      <InfoRow label="Description" value={activity.description} />
                      <InfoRow label="NIC Code" value={activity.nic_code} />
                      <InfoRow label="Industry" value={activity.industry} />
                    </div>
                  ))}
                </div>
              )}

              {/* Registrations Section */}
              {activeSection === 'registrations' && (
                <div className="bg-white rounded-xl border border-border p-5 sm:p-6">
                  <h2 className="font-semibold text-ink text-[15px] mb-5">Registrations</h2>
                  <InfoRow label="GSTIN" value={business.gstin} placeholder="Not registered" />
                  <InfoRow label="MSME / Udyam" value={business.business_passports?.msme_number || null} placeholder="Not registered" />
                  <InfoRow label="DPIIT" value={business.business_passports?.dpiit_number || null} placeholder="Not recognized" />
                  <InfoRow label="CIN" value={business.cin} placeholder="Pending incorporation" />

                  <div className="mt-5 pt-5 border-t border-border">
                    <p className="text-[12px] text-ink-muted mb-3">Quick actions</p>
                    <div className="flex flex-wrap gap-2">
                      {!business.gstin && <Link href="/gst" className="text-[12px] font-semibold text-ai bg-ai-light px-3 py-1.5 rounded-lg hover:bg-ai/15 transition-colors">Register for GST →</Link>}
                      {!business.business_passports?.msme_number && <Link href="/msme" className="text-[12px] font-semibold text-ai bg-ai-light px-3 py-1.5 rounded-lg hover:bg-ai/15 transition-colors">Register MSME →</Link>}
                      {!business.business_passports?.dpiit_number && <Link href="/dpiit" className="text-[12px] font-semibold text-ai bg-ai-light px-3 py-1.5 rounded-lg hover:bg-ai/15 transition-colors">Apply for DPIIT →</Link>}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
